export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

/**
 * Formats a number as a percentage string.
 *
 * @param value - The value to format (e.g. 0.1234).
 * @param options - Optional formatting options.
 * @param options.fractionDigits - Number of decimal places (default: 0).
 * @param options.includePercentageSign - Whether to include the '%' sign (default: true).
 * @returns The formatted percentage string.
 *
 * @example
 * formatPercentage(0.1234) // "12%"
 * formatPercentage(0.1234, { fractionDigits: 2 }) // "12.34%"
 * formatPercentage(0.1234, { includePercentageSign: false }) // "12"
 * formatPercentage(NaN) // "0%"
 */
export const formatPercentage = (
  value: number,
  {
    fractionDigits = 0,
    includePercentageSign = true,
  }: {
    fractionDigits?: number
    includePercentageSign?: boolean
  } = {}
) => {
  const percentage = toPercentageValue(value).toFixed(fractionDigits)

  return `${percentage}${includePercentageSign ? '%' : ''}`
}

/**
 * Converts a decimal value to a percentage value (0-100).
 * If the input is NaN, returns 0.
 * If the input is greater than 1, returns 1 (i.e., 100%).
 *
 * @example
 * toPercentageValue(0.25) // 25
 * toPercentageValue(1.2)  // 1
 * toPercentageValue(NaN)  // 0
 *
 * @param value - The decimal value to convert.
 * @returns The percentage value with decimals (0-100).
 */
const toPercentageValue = (value: number) => {
  if (isNaN(value)) {
    return 0
  }
  // No negative percentage values
  if (value < 0) {
    return 0
  }
  // No percentage can be greater than 100%
  if (value > 1) {
    return 100
  }
  return value * 100
}

// TODO: Replace this with the pluralize function from our i18n library once we start localizing.
// Note that not all languages use the same pluralization rules--in English we just have singular and plural,
// but in other languages we have more options.
export const pluralize = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural)

/** Join the non-empty segments with a middot separator, dropping null/undefined/empty. */
/**
 * A human list — "v1", "v1 and v2", "v1, v2 and v3".
 *
 * Hand-rolled rather than `Intl.ListFormat`, which this package's `lib` does not include: widening the
 * shared tsconfig to format one caption is a worse trade than four lines here. No Oxford comma, matching
 * the rest of the product's copy.
 */
export const joinWithAnd = (items: string[]): string => {
  if (items.length <= 2) {
    return items.join(' and ')
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

export const joinMetaSegments = (segments: (string | null | undefined)[]): string =>
  segments.filter(segment => !!segment).join(' · ')

/** Join non-empty copy sections with a blank line between them (the drawers' clipboard convention). */
export const joinCopySections = (...sections: (string | undefined)[]) => sections.filter(Boolean).join('\n\n')

// ES2025 introduces a new function `RegExp.escape` that we should use instead of this once we can target that version.
export const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const toKebabCase = (str: string) => {
  return str
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/[^\w-]+/g, '') // Remove non-word characters except hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

const POSITIVE_INTEGER_PATTERN = /^\d+$/

/**
 * Reads a positive integer out of a text field. Plain decimal digits only, and within the range
 * JavaScript holds exactly: `Number` on its own reads `1e5` and `0x1f` as integers and rounds a
 * 17-digit entry to a neighbouring value, silently naming something other than what was typed.
 *
 * @returns the integer, or undefined when the value is empty or not a whole number greater than zero
 */
export const parsePositiveInteger = (value: string | undefined): number | undefined => {
  const trimmed = (value ?? '').trim()
  if (!POSITIVE_INTEGER_PATTERN.test(trimmed)) {
    return undefined
  }
  const parsed = Number(trimmed)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined
}
